import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { APIService } from 'src/app/core/api/general.service';
import { ActionTypeText, OptionType } from 'src/app/core/enums/enums';
import { SearchForm } from 'src/app/core/models/itinerary.model';
import { ContractError } from 'src/app/core/models/models';
import { GenericError } from 'src/app/layout/actions/layout.actions';
import { PnrActions } from '../actions';
import { LayoutService } from 'src/app/layout/services/layout.service';
import { AuthFactoryService } from 'src/app/auth/authFactory.service';
import { ReportLogService } from 'src/app/shared/services/report-log.service';
import { ItineraryService } from '../services/itinerary.service';
import { Config } from 'src/environments/config.model';
import { environment } from 'src/environments/environment';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class PnrEffects {
  env: Config = environment;
  params: SearchForm;
  mockError: ContractError = {
    message: 'Unknown error',
    actionName: '',
    controllerName: '',
    errorCode: 400,
    stackTrace: '',
    alertType: null
  };

  constructor(
    private authFactoryService: AuthFactoryService,
    private generalservice: APIService,
    private actions$: Actions,
    private route: ActivatedRoute,
    private layoutService: LayoutService,
    private reportLog: ReportLogService,
    private itineraryService: ItineraryService
  ) {
    this.getParams();
  }

  options$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PnrActions.LoadAvailableOption),
      concatMap((action) =>
        of(action).pipe(withLatestFrom(this.layoutService.getBookingCredentialsState()))
      ),
      mergeMap(([action, credentials]) => {
        if (action.quoteRequest?.pnrOption === OptionType.VoluntaryChange) {
          action.quoteRequest.overSign = credentials.data;
        }

        return this.generalservice.getAvailableOption(action.quoteRequest).pipe(
          map((pnrAvailableOption) => {
            return PnrActions.LoadAvailableOptionSuccess({
              pnrAvailableOption,
              machineLearningEnabled: false
            });
          }),
          catchError((response) => {
            return of(GenericError({ error: response.error }));
          })
        );
      })
    )
  );

  splitPnr$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PnrActions.SplitPnrRequest),
      concatMap((action) => of(action).pipe(withLatestFrom(this.itineraryService.getPnrInfo()))),
      mergeMap(([action, pnrInfo]) => {
        const quote = action?.quoteRequest;
        quote.transactionId = pnrInfo?.transactionId;

        return this.generalservice.splitPNRv2(quote).pipe(
          mergeMap((pnr) => {
            return [PnrActions.SplitPnrSuccess({ pnr })];
          }),
          catchError((response) =>
            of(
              PnrActions.SplitPnrFail({
                splitError: response.error
              })
            )
          )
        );
      })
    )
  );

  forceRefreshSesionAndVoid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PnrActions.ForceRefreshSesionAndVoid),
      mergeMap((action) =>
        this.authFactoryService.forceRefreshSession().pipe(
          map(() => {
            return PnrActions.VoidTransaction({ quoteRequest: action.quoteRequest });
          }),
          catchError((response) => {
            window.scroll(0, 0);
            return of(
              PnrActions.VoidTransactionFail({
                error: response.error,
                message: response.error.message
              })
            );
          })
        )
      )
    )
  );

  dispatchVoidTransaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PnrActions.VoidTransaction),
      switchMap((payload) =>
        this.generalservice.postVoidTransaction(payload.quoteRequest).pipe(
          map((response) => {
            this.reportLog.Success(ActionTypeText.Cancellation);
            if (response.status > 199 && response.status < 300) {
              return PnrActions.VoidTransactionSuccess({ response: response.body });
            } else {
              return PnrActions.VoidTransactionFail({
                error: this.mockError,
                message: this.mockError.message
              });
            }
          }),
          catchError((response) => {
            this.reportLog.Failure(ActionTypeText.Cancellation, response.error);
            return of(
              PnrActions.VoidTransactionFail({
                error: response.error,
                message: response.error.message
              })
            );
          })
        )
      )
    )
  );

  LoadPenaltyTextAI$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PnrActions.LoadPenaltyTextAI),
      mergeMap((action) =>
        this.generalservice.getPenaltyTextAI(action.quoteRequest).pipe(
          map((penalty) => PnrActions.LoadPenaltyTextAISuccess({ penalty })),
          catchError((response) => {
            return of(GenericError({ error: response.error }));
          })
        )
      )
    )
  );

  LoadRulesAI$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PnrActions.LoadRulesIA),
      mergeMap((action) =>
        this.generalservice.getPenaltyTextAI(action?.quoteRequest, OptionType.Availability).pipe(
          map((penalty) => PnrActions.LoadRulesIASuccess({ penalty })),
          catchError((error) => {
            return of(PnrActions.LoadRulesIAFail({ error }));
          })
        )
      )
    )
  );

  LoadSRequestValidationPerFlight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PnrActions.SRequestValidationPerFlight),
      mergeMap((action) =>
        this.generalservice.postSRequestValidationPerFlight(action.quoteRequest).pipe(
          map((response) =>
            PnrActions.SRequestValidationPerFlightSuccess({ response: response.body })
          ),
          catchError((error) => {
            return of(PnrActions.SRequestValidationPerFlightFailed({ error }));
          })
        )
      )
    )
  );

  GetAdditionalFlightInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PnrActions.GetAdditionalFlightInfo),
      mergeMap((action) =>
        this.generalservice.getAdditionalFlightInfo(action.requestAdditionalFlightInfo).pipe(
          map((additionalFlightInfo) =>
            PnrActions.GetAdditionalFlightInfoSuccess({
              additionalFlightInfo: additionalFlightInfo[0]
            })
          ),
          catchError((response) => {
            return of(GenericError({ error: response.error }));
          })
        )
      )
    )
  );

  GetOAGFLTStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PnrActions.GetOAGFLTStatus),
      concatMap((action) => of(action).pipe(withLatestFrom(this.itineraryService.getPnrInfo()))),
      mergeMap(([action, pnrInfo]) =>
        this.generalservice.getOAGFLTStatus(pnrInfo.code, pnrInfo.gds).pipe(
          map((OAGFLTStatusResponse) =>
            PnrActions.GetOAGFLTStatusSuccess({ OAGFLTStatus: OAGFLTStatusResponse.body })
          ),
          catchError((error) => {
            return of(PnrActions.GetOAGFLTStatusFailed({ error }));
          })
        )
      )
    )
  );

  RegisterUserLogInLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PnrActions.RegisterUserLogInLog),
      concatMap((action) => of(action).pipe(withLatestFrom(of(this.authFactoryService.getUser())))),
      mergeMap(([action, userInfo]) =>
        this.generalservice.postLogInLog(userInfo?.userName).pipe(
          map((response) => PnrActions.RegisterUserLogInLogSuccess()),
          catchError((error: HttpErrorResponse) => {
            return of(PnrActions.RegisterUserLogInLogFailed({ error: error.message }));
          })
        )
      )
    )
  );

  private getParams() {
    this.route.queryParams.subscribe((params: SearchForm) => {
      this.params = params;
    });
  }
}
